// 후속 #4 (2026-05-26) — 외부 바이어 전체매물 차량 상세 페이지 (/buyer/inventory/:id)
// 백엔드: GET /buyer-app/inventory/:id (getInventoryCar — masked card + photos[]).
// 사진 갤러리(AuthImage blob) + 전체 스펙 + 광고가 KRW/USD + 출품업체 + 문의.

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { API } from '../../utils/api';
import { authBuyerFetch, isUnauthorized, handleUnauthorized, displayApiError } from './buyerApi';
import AuctionBuyerLayout from './AuctionBuyerLayout';
import AuthImage from './AuthImage';
import PhotoViewerModal from './PhotoViewerModal';
import InventoryInquiryModal from './InventoryInquiryModal';
import CarPaintMap from '../CarPaintMap';
import { useT } from '../../i18n/I18nProvider';
import { formatKrwAmountWithUsd, getUsdKrwRate, useExchangeRates } from '../../i18n/currency';
import TrustSpeedBadge from '../TrustSpeedBadge';
import { gradeI18nKey } from '../../utils/trustSpeedGrades';

// 검차 요약 키(백엔드 summaryKey) → i18n 키
var INSPECT_SUMMARY_I18N = {
  accidentFree: 'invInspectAccidentFree',
  minorAccident: 'invInspectMinorAccident',
  accident: 'invInspectAccident'
};

function specRow(label, value) {
  if (value == null || value === '') return null;
  return (
    <div key={label} style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid #f1f5f9', fontSize: 14 }}>
      <span style={{ color: '#64748b' }}>{label}</span>
      <span style={{ color: '#0f172a', fontWeight: 600 }}>{value}</span>
    </div>
  );
}

function AuctionBuyerInventoryDetail() {
  var navigate = useNavigate();
  var params = useParams();
  var carId = params.id;
  var t = useT();
  var rateState = useExchangeRates();

  var [car, setCar] = useState(null);
  var [loading, setLoading] = useState(true);
  var [error, setError] = useState('');
  var [photoIdx, setPhotoIdx] = useState(0);
  var [contactOpen, setContactOpen] = useState(false);
  var [viewerOpen, setViewerOpen] = useState(false);

  var load = useCallback(function() {
    setLoading(true);
    setError('');
    authBuyerFetch(API + '/buyer-app/inventory/' + carId, {})
      .then(function(r) {
        if (isUnauthorized(r)) { handleUnauthorized(navigate); throw new Error('unauthorized'); }
        return r.json();
      })
      .then(function(d) {
        if (d && d.success && d.data) setCar(d.data);
        else setError(displayApiError(d, t, 'invDetailNotFound'));
        setLoading(false);
      })
      .catch(function(e) {
        if (e && e.message === 'unauthorized') return;
        setError(t('commonNetwork'));
        setLoading(false);
      });
  }, [carId, navigate, t]);

  useEffect(function() { load(); }, [load]);

  if (loading) {
    return (
      <AuctionBuyerLayout activeKey="inventory">
        <div style={{ textAlign: 'center', padding: 80, color: '#94a3b8' }}>{t('commonLoading')}</div>
      </AuctionBuyerLayout>
    );
  }
  if (error || !car) {
    return (
      <AuctionBuyerLayout activeKey="inventory">
        <div style={{ textAlign: 'center', padding: 80, color: '#dc2626' }}>
          {error || t('invDetailNotFound')}
          <div style={{ marginTop: 16 }}>
            <button type="button" onClick={function(){ navigate('/buyer/inventory'); }}
              style={{ padding: '10px 20px', borderRadius: 8, border: 'none', background: '#1B3A5C', color: 'white', fontSize: 14, fontWeight: 700, cursor: 'pointer' }}>
              {t('invDetailBack')}
            </button>
          </div>
        </div>
      </AuctionBuyerLayout>
    );
  }

  var photos = car.photos || [];
  var photoCount = photos.length;
  var mainPhoto = photoCount > 0 ? photos[Math.min(photoIdx, photoCount - 1)] : null;
  var specParts = [];
  if (car.mileage) specParts.push(Number(car.mileage).toLocaleString('en-US') + ' km');

  return (
    <AuctionBuyerLayout activeKey="inventory">
      <div style={{ maxWidth: 1200, margin: '0 auto', padding: '24px 32px 100px' }}>
        <button type="button" onClick={function(){ navigate('/buyer/inventory'); }}
          style={{ background: 'none', border: 'none', color: '#64748b', fontSize: 13, fontWeight: 600, cursor: 'pointer', padding: '8px 0', marginBottom: 16 }}>
          {t('invDetailBack')}
        </button>

        <div className="buyer-detail-grid">
          {/* 좌측 — 사진 갤러리 + 스펙 */}
          <div>
            <div style={{ background: 'white', borderRadius: 12, overflow: 'hidden', border: '1px solid #e2e8f0' }}>
              {mainPhoto ? (
                <div onClick={function(){ setViewerOpen(true); }} style={{ cursor: 'zoom-in' }}>
                  <AuthImage path={mainPhoto.url} alt={car.carType}
                    style={{ width: '100%', aspectRatio: '4/3', objectFit: 'cover', display: 'block', background: '#f1f5f9' }} />
                </div>
              ) : (
                <div style={{ width: '100%', aspectRatio: '4/3', background: '#f1f5f9', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 48, color: '#cbd5e1' }}>🚗</div>
              )}
              {photoCount > 1 && (
                <div style={{ display: 'flex', gap: 4, padding: 8, overflowX: 'auto', borderTop: '1px solid #f1f5f9' }}>
                  {photos.map(function(p, i) {
                    return (
                      <button key={i} type="button" onClick={function(){ setPhotoIdx(i); }}
                        style={{ flex: '0 0 80px', width: 80, height: 60, padding: 0, border: photoIdx === i ? '2px solid #1B3A5C' : '1px solid #e2e8f0', borderRadius: 6, overflow: 'hidden', cursor: 'pointer', background: 'white' }}>
                        <AuthImage path={p.url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
                      </button>
                    );
                  })}
                </div>
              )}
            </div>

            <div style={{ background: 'white', borderRadius: 12, padding: 24, border: '1px solid #e2e8f0', marginTop: 16 }}>
              <h2 style={{ fontSize: 22, fontWeight: 800, color: '#0f172a', marginBottom: 16 }}>{car.carType || '-'}</h2>
              <div style={{ fontSize: 12, fontWeight: 700, color: '#94a3b8', marginBottom: 4 }}>{t('invDetailSpecs')}</div>
              {specRow(t('detailCarNumberLabel'), car.number)}
              {specRow(t('detailMileageLabel'), car.mileage != null ? Number(car.mileage).toLocaleString('en-US') + ' km' : null)}
              {specRow(t('detailFuelLabel'), car.fuel)}
              {specRow(t('detailColorLabel'), car.color)}
              {specRow(t('invDetailDisplacement'), car.displacement)}
              {specRow(t('invDetailUsage'), car.usage2)}
              {specRow('VIN', car.vin)}
            </div>

            {/* 검차 정보 — 도막(도색) + 사고 판정 + 종합점수 (외부 바이어 신뢰 신호) */}
            {car.inspection && car.inspection.hasInspection && (
              <div style={{ background: 'white', borderRadius: 12, padding: 24, border: '1px solid #e2e8f0', marginTop: 16 }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: '#94a3b8', marginBottom: 12 }}>{t('invInspectTitle')}</div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', marginBottom: 16 }}>
                  <span style={{ padding: '6px 14px', background: car.inspection.summaryColor || '#94a3b8', color: '#fff', borderRadius: 6, fontSize: 13, fontWeight: 700 }}>
                    {t(INSPECT_SUMMARY_I18N[car.inspection.summaryKey] || 'invInspectAccidentFree')}
                  </span>
                  {car.inspection.score > 0 && (
                    <span style={{ fontSize: 13, color: '#475569' }}>
                      {t('invInspectScore')} <strong style={{ color: '#0f172a', fontSize: 15 }}>{car.inspection.score}</strong>/100
                    </span>
                  )}
                </div>
                {car.inspection.paintMap && Object.keys(car.inspection.paintMap).filter(function(k){ return car.inspection.paintMap[k]; }).length > 0 && (
                  <div>
                    <div style={{ fontSize: 12, color: '#64748b', marginBottom: 8 }}>{t('invInspectPaintTitle')}</div>
                    <div style={{ display: 'flex', justifyContent: 'center' }}>
                      <CarPaintMap data={car.inspection.paintMap} width={300} />
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* 우측 — 가격/문의 sticky 카드 */}
          <div style={{ position: 'sticky', top: 24 }}>
            <div style={{ background: 'white', borderRadius: 12, padding: 24, border: '1px solid #e2e8f0' }}>
              <div style={{ fontSize: 12, color: '#94a3b8', fontWeight: 600 }}>{t('invColPrice')}</div>
              <div style={{ fontSize: 24, fontWeight: 800, color: '#1B3A5C', marginTop: 4 }}>
                {car.priceKrw != null
                  ? formatKrwAmountWithUsd(car.priceKrw, t, rateState.rates)
                  : <span style={{ color: '#0ea5e9', fontSize: 16 }}>{t('invPriceNegotiable')}</span>}
              </div>
              {getUsdKrwRate(rateState.rates) != null && (
                <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 6 }}>
                  {t('invFxRateToday', { rate: getUsdKrwRate(rateState.rates).toLocaleString() })}
                  {rateState.isStale ? <span style={{ marginLeft: 6, color: '#f59e0b' }}>{t('currencyStaleMarker')}</span> : null}
                </div>
              )}

              <button type="button" onClick={function(){ setContactOpen(true); }}
                style={{ width: '100%', height: 52, background: 'linear-gradient(135deg, #1B3A5C 0%, #3b82f6 100%)', color: 'white', border: 'none', borderRadius: 10, fontSize: 15, fontWeight: 700, cursor: 'pointer', marginTop: 20, boxShadow: '0 4px 12px rgba(27,58,92,0.2)' }}>
                {t('invContactCta')}
              </button>

              {car.seller && (car.seller.companyName || car.seller.companyNameEn) && (
                <div style={{ marginTop: 20, paddingTop: 20, borderTop: '1px solid #f1f5f9' }}>
                  <div style={{ fontSize: 11, color: '#94a3b8', fontWeight: 600, marginBottom: 6 }}>{t('detailSeller')}</div>
                  <div style={{ fontSize: 14, fontWeight: 700, color: '#0f172a' }}>{car.seller.companyNameEn || car.seller.companyName}</div>
                  {(car.seller.trustSpeedGrade || car.seller.completedDeals > 0) && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap', marginTop: 6 }}>
                      {car.seller.trustSpeedGrade && (
                        <TrustSpeedBadge grade={car.seller.trustSpeedGrade} speed={car.seller.trustSpeed}
                          label={t(gradeI18nKey(car.seller.trustSpeedGrade))} size="md" />
                      )}
                      {car.seller.completedDeals > 0 && (
                        <span style={{ fontSize: 12, color: '#64748b', fontWeight: 700 }}>{t('completedDealsCount', { count: car.seller.completedDeals })}</span>
                      )}
                    </div>
                  )}
                  {car.seller.joinedAt && (function(){
                    var d = new Date(String(car.seller.joinedAt).replace(' ', 'T'));
                    if (isNaN(d.getTime())) return null;
                    return <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 4 }}>{t('detailSellerJoinedYM', { year: d.getFullYear(), month: d.getMonth() + 1 })}</div>;
                  })()}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      {contactOpen && <InventoryInquiryModal car={car} t={t} navigate={navigate} onClose={function(){ setContactOpen(false); }} />}
      {viewerOpen && photoCount > 0 && (
        <PhotoViewerModal pathBase={'/buyer-app/inventory/' + car.id} count={photoCount} startIdx={photoIdx}
          onClose={function(){ setViewerOpen(false); }} />
      )}
    </AuctionBuyerLayout>
  );
}

export default AuctionBuyerInventoryDetail;
